import { Component, type PropsWithChildren } from "react";
import type { NavigateFunction } from "react-router-dom-v5-compat";
import type { ErrorBoundaryFCProps } from ".";

const PATH_HOME = "/";

type ErrorBoundaryProps = {
  navigate: NavigateFunction;
} & ErrorBoundaryFCProps;

export class ErrorBoundaryClass extends Component<
  PropsWithChildren<ErrorBoundaryProps>,
  { hasError: boolean }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary", error, errorInfo);
  }

  componentDidUpdate(
    prevProps: ErrorBoundaryProps,
    prevState: { hasError: boolean }
  ) {
    // Handle navigation outside of render to avoid setState warnings
    if (this.state.hasError && !prevState.hasError) {
      const { navigate, errorPath } = this.props;
      navigate(errorPath ?? PATH_HOME);
      this.reset();
    }
  }

  reset() {
    this.setState({ hasError: false });
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

export default ErrorBoundaryClass;
